import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useAlert } from "../contextProviders/AlertProvider";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskAssessmentHistoryDTO, RiskUserDTO } from "../../types/RiskAssessmentDTO";
import { readErrorMessage } from "./riskErrors";
import RiskSpinner from "./RiskSpinner";
import './RiskAssignModal.scss';

const STAGE_MAX_LENGTH = 100;

interface Props {
	item: RiskAssessmentHistoryDTO | null;
	onClose: () => void;
	onAssigned: (item: RiskAssessmentHistoryDTO) => void;
}

const RiskAssignModal: FC<Props> = ({ item, onClose, onAssigned }) => {
	const [users, setUsers] = useState<RiskUserDTO[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [responsible, setResponsible] = useState<RiskUserDTO | null>(null);
	const [stage, setStage] = useState('');
	const [saving, setSaving] = useState(false);
	const { showAlert, showError } = useAlert();

	useEffect(() => {
		if (!item) {
			return;
		}
		setResponsible(item.responsibleUsername ? { username: item.responsibleUsername, name: item.responsibleName || item.responsibleUsername } : null);
		setStage(item.stage || '');
		const loadUsers = async () => {
			setLoadingUsers(true);
			const response = await new RiskAssessmentService().getUsers();
			if (!response) {
				showError('Não foi possível carregar os usuários internos.');
			}
			setUsers(response?.data || []);
			setLoadingUsers(false);
		};
		loadUsers();
	}, [item]);

	return (
		<Dialog
			open={!!item}
			onClose={saving ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			id="modal-risk-assign"
			className="risk-assign-modal"
		>
			<DialogTitle>Responsável e etapa</DialogTitle>
			<DialogContent>
				<Typography className="descricao">
					Somente o responsável consegue continuar a edição e alterar o responsável ou a etapa. Ao passar a
					avaliação para outro usuário, você deixa de poder editá-la.
				</Typography>
				<div className="campos">
					<Autocomplete
						options={users}
						loading={loadingUsers}
						value={responsible}
						getOptionLabel={(option) => option.name ? `${option.name} (${option.username})` : option.username}
						isOptionEqualToValue={(option, value) => option.username === value.username}
						onChange={(_, next) => setResponsible(next)}
						noOptionsText="Nenhum usuário interno encontrado"
						renderInput={(params) => <TextField {...params} label="Responsável *"/>}
					/>
					<TextField
						label="Etapa"
						placeholder="Ex.: Parte 2 - cargos administrativos"
						value={stage}
						inputProps={{ maxLength: STAGE_MAX_LENGTH }}
						onChange={(event) => setStage(event.target.value)}
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<Button id="risk-assign-close-button" disabled={saving} onClick={onClose}>Voltar</Button>
				<Button
					id="risk-assign-confirm-button"
					variant="contained"
					startIcon={saving ? <RiskSpinner size={18}/> : undefined}
					disabled={!responsible || saving}
					onClick={save}
				>
					Salvar
				</Button>
			</DialogActions>
		</Dialog>
	);

	async function save() {
		if (!item || !responsible) {
			return;
		}
		setSaving(true);
		try {
			const response = await new RiskAssessmentService().assignDraft(item.id, responsible.username, stage);
			onAssigned(response.data);
			showAlert('Responsável e etapa atualizados.');
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível atualizar o responsável.'));
		} finally {
			setSaving(false);
		}
	}
};

export default RiskAssignModal;